import { SagaIterator } from 'redux-saga';
import { call, delay, put, takeEvery } from 'redux-saga/effects';
import {
  cancelSubscription,
  createPayment,
  getCurrentPlan,
  getCustomerPortal,
  getPaymentUsage,
} from '../../services/paymentService/paymentService';
import {
  cancelSubscriptionFailure,
  getCurrentPlanFailure,
  getCurrentPlanRequest,
  getCurrentPlanSuccess,
  getPaymentUsageRequest,
  cancelSubscriptionSuccess,
  createPaymentFailure,
  createPaymentSuccess,
  customerPortalFailure,
  customerPortalSuccess,
  getPaymentUsageFailure,
  getPaymentUsageSuccess,
} from './payments.action';
import {
  CreatePaymentRequestAction,
  CurrentPlanData,
  PaymentUsageData,
  PaymentsActionTypes,
} from './payments.types';

function* createPaymentSaga(action: CreatePaymentRequestAction): SagaIterator {
  try {
    const response: {
      checkoutUrl?: string;
      message?: string;
      changeType?: string;
    } = yield call(createPayment, {
      product: action.payload.product,
    });

    if (response.checkoutUrl) {
      yield put(createPaymentSuccess({ checkoutUrl: response.checkoutUrl }));
      window.location.href = response.checkoutUrl;
      return;
    }

    if (response.message && response.changeType) {
      // Some plan changes are applied directly without a checkout redirect URL.
      yield put(
        createPaymentSuccess({
          message: response.message,
          changeType: response.changeType,
        })
      );
      return;
    }

    if (!response.checkoutUrl) {
      throw new Error('Checkout URL is missing in payment response');
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to create payment';
    yield put(createPaymentFailure(errorMessage));
  }
}

function* getCurrentPlanSaga(): SagaIterator {
  try {
    const response: CurrentPlanData = yield call(getCurrentPlan);
    yield put(getCurrentPlanSuccess(response));
  } catch (error: any) {
    const httpStatus = error.response?.status;
    if (httpStatus === 404 || httpStatus === 204) {
      yield put(getCurrentPlanSuccess({}));
      return;
    }
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch current plan';
    yield put(getCurrentPlanFailure(errorMessage));
  }
}

function* getPaymentUsageSaga(): SagaIterator {
  try {
    const response: PaymentUsageData = yield call(getPaymentUsage);
    yield put(getPaymentUsageSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch payment usage';
    yield put(getPaymentUsageFailure(errorMessage));
  }
}

function* cancelSubscriptionSaga(): SagaIterator {
  try {
    const response: { status?: string } = yield call(cancelSubscription);

    if (!response.status) {
      throw new Error(
        'Checkout URL is missing in cancel subscription response'
      );
    }

    yield put(cancelSubscriptionSuccess(response.status));
    yield delay(2000);
    yield put(getPaymentUsageRequest());
    yield put(getCurrentPlanRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to cancel subscription';
    yield put(cancelSubscriptionFailure(errorMessage));
  }
}

function* customerPortalSaga(): SagaIterator {
  try {
    const response: { checkoutUrl?: string; url?: string } =
      yield call(getCustomerPortal);
    const portalUrl = response.checkoutUrl || response.url;

    if (!portalUrl) {
      throw new Error('Customer portal URL is missing in payment response');
    }

    yield put(customerPortalSuccess(portalUrl));
    window.location.href = portalUrl;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to open customer portal';
    yield put(customerPortalFailure(errorMessage));
  }
}

export function* paymentsSaga(): SagaIterator {
  yield takeEvery(
    PaymentsActionTypes.CREATE_PAYMENT_REQUEST,
    createPaymentSaga
  );
  yield takeEvery(
    PaymentsActionTypes.GET_CURRENT_PLAN_REQUEST,
    getCurrentPlanSaga
  );
  yield takeEvery(
    PaymentsActionTypes.GET_PAYMENT_USAGE_REQUEST,
    getPaymentUsageSaga
  );
  yield takeEvery(
    PaymentsActionTypes.CANCEL_SUBSCRIPTION_REQUEST,
    cancelSubscriptionSaga
  );
  yield takeEvery(
    PaymentsActionTypes.CUSTOMER_PORTAL_REQUEST,
    customerPortalSaga
  );
}
